import { useMemo } from 'react'
import { Address } from 'use-towns-client'

import { SPACE_METADATA_TEXT_FIELD_KEYS as TOWN_METADATA_TEXTFIELDS } from '../constants/spaceMetadata'
import { ServerSpaceMetadata, TownSocialLink } from '../schema/spaceMetadata.schema'
import { parseJsonField } from '../utils/editSpaceMetadataUtils'

import { useFetchSpaceMetadata } from './useFetchSpaceMetadata'

type TownSetupChecklistItemKey =
    | 'createTown'
    | 'uploadMedia'
    | 'whatMembersGet'
    | 'aboutTown'
    | 'aboutCreator'

type TownSetupChecklistItem = {
    key: TownSetupChecklistItemKey
    label: string
    completed: boolean
}

export type SpaceMetadataSetupProgress = {
    items: TownSetupChecklistItem[]
    percentComplete: number
    isComplete: boolean
}

const defaultProgress: SpaceMetadataSetupProgress = {
    items: [
        { key: 'createTown', label: 'Create a town', completed: true },
        { key: 'uploadMedia', label: 'Upload media', completed: false },
        { key: 'aboutTown', label: 'About the town', completed: false },
        { key: 'aboutCreator', label: 'About the creator', completed: false },
        { key: 'whatMembersGet', label: 'What members get', completed: false },
    ],
    percentComplete: 20,
    isComplete: false,
}

export function useSpaceMetadataProgress(
    spaceAddress: Address | undefined,
    options?: { enabled?: boolean },
) {
    const { data } = useFetchSpaceMetadata(spaceAddress, options)
    const progress = useMemo(() => buildSpaceMetadataProgress(data), [data])
    return {
        data,
        progress,
    }
}

export function buildSpaceMetadata(metadata?: ServerSpaceMetadata) {
    const textFields = metadata?.textFields ?? {}

    const category = metadata?.category
    const media = metadata?.media ?? []
    const motto = textFields[TOWN_METADATA_TEXTFIELDS.motto]?.trim() ?? ''
    const aboutTown = textFields[TOWN_METADATA_TEXTFIELDS.aboutTown]?.trim() ?? ''
    const aboutCreator = textFields[TOWN_METADATA_TEXTFIELDS.aboutCreator]?.trim() ?? ''
    const creatorDisplayName = textFields[TOWN_METADATA_TEXTFIELDS.creatorDisplayName]?.trim() ?? ''
    const features = parseJsonField<string[]>(
        textFields[TOWN_METADATA_TEXTFIELDS.features],
        [],
    ).filter((feature) => feature.trim().length > 0)

    const socialLinks = parseJsonField<TownSocialLink[]>(
        textFields[TOWN_METADATA_TEXTFIELDS.socialLinks],
        [],
    ).filter((link) => link.url.trim().length > 0)

    const faq = metadata?.faq ?? []

    return {
        category,
        media,
        motto,
        features,
        aboutTown,
        aboutCreator,
        creatorDisplayName,
        socialLinks,
        faq,
    }
}

export function buildSpaceMetadataProgress(
    metadata?: ServerSpaceMetadata,
): SpaceMetadataSetupProgress {
    if (!metadata) {
        return defaultProgress
    }

    const hasMedia = (metadata.media?.length ?? 0) > 0

    const { aboutTown, aboutCreator, features } = buildSpaceMetadata(metadata)

    const items: TownSetupChecklistItem[] = [
        { key: 'createTown', label: 'Create a town', completed: true },
        { key: 'uploadMedia', label: 'Upload media', completed: hasMedia },
        { key: 'aboutTown', label: 'About the town', completed: aboutTown.length > 0 },
        { key: 'aboutCreator', label: 'About the creator', completed: aboutCreator.length > 0 },
        { key: 'whatMembersGet', label: 'What members get', completed: features.length > 0 },
    ]

    const completedCount = items.filter((item) => item.completed).length
    const percentComplete = Math.round((completedCount / items.length) * 100)

    return {
        items,
        percentComplete,
        isComplete: percentComplete === 100,
    }
}
